/**
 * Pickup date/time rules for the client booking flow — shared so the Date/Time panel and the
 * (future) final booking submission validate identically and can't drift apart.
 *
 * A pickup is an absolute timestamp, so a time that was valid when picked can go stale if the tab is
 * left open (it slips into the past, or under the 5-minute lead). `validatePickup` must therefore run
 * BOTH at the panel's Proceed AND again immediately before the booking is created (final submit),
 * each time against a fresh `now`. The backend guarder (AustralianGuarder / USGuarder) is the final
 * backstop; this keeps the client from ever firing a request the server will reject.
 */

/** Pickup times snap to a 5-minute grid — matches the old web/mobile booking flows and the backend. */
export const STEP_MINUTES = 5;
/** Backend rejects pickups less than 5 minutes away (AustralianGuarder / USGuarder). */
export const MIN_LEAD_MS = 5 * 60 * 1000;
/**
 * The current local time, snapped to the nearest 5-min mark (the grid the minute field uses) — the
 * pre-filled default pickup. Matches the mobile app, which defaults the picker to "now" rather than
 * adding a lead. If the user proceeds immediately, `validatePickup` still enforces the 5-min-lead rule
 * and prompts them to pick a later time.
 */
export const defaultPickupSlot = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  const rem = d.getMinutes() % STEP_MINUTES;
  // Round to the nearest step (matches the minute field's own snapping) so the default reads as "now".
  if (rem !== 0) d.setMinutes(d.getMinutes() + (rem < STEP_MINUTES / 2 ? -rem : STEP_MINUTES - rem));
  return d;
};

/**
 * Validate a fully-resolved pickup timestamp against the not-past + 5-min-lead rules the backend
 * enforces. Returns a user-facing error message, or `null` when the pickup is valid.
 */
export const validatePickup = (pickup: Date, now: number = Date.now()): string | null => {
  if (pickup.getTime() < now) return "Pickup time can't be in the past";
  if (pickup.getTime() < now + MIN_LEAD_MS) {
    return "Your booking is less than 5 minutes away. Please contact us to make this booking.";
  }
  return null;
};
